import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  signal,
  computed
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DrillPlanService } from '../../services/drill-plan.service';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { DrillPlanImportResult, DrillPlanPlan, DrillPlanProject } from '../../models/drill-plan.models';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-drill-plan-import',
  templateUrl: './drill-plan-import.component.html',
  standalone: false
})
export class DrillPlanImportComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() importComplete = new EventEmitter<DrillPlanImportResult>();

  public readonly isCheckingConnection = signal(false);
  public readonly isConnected = signal(false);
  public readonly isConnecting = signal(false);
  public readonly isImporting = signal(false);
  public readonly isDownloadingArtifacts = signal(false);
  public readonly isLoadingProjects = signal(false);
  public readonly isLoadingPlans = signal(false);
  public readonly importResult = signal<DrillPlanImportResult | null>(null);

  public readonly isDevEnvironment = !environment.production;

  public readonly projects = signal<DrillPlanProject[]>([]);
  public readonly plans = signal<DrillPlanPlan[]>([]);
  public readonly selectedProjectId = signal<string | null>(null);
  public readonly selectedPlanId = signal<string | null>(null);

  public readonly importTrajectory = signal(true);
  public readonly importWellboreGeometry = signal(true);
  public readonly importFormationMarkers = signal(true);
  public readonly importPressureWindow = signal(false);
  public readonly importTemperature = signal(false);

  public readonly importOptionDetails = {
    trajectory: 'Survey stations (MD, inclination, azimuth).',
    wellConfiguration: 'Tubular strings, sections, and geometry mapping.',
    formationMarkers: 'Formation tops mapped from DrillPlan markers.',
    pressureWindow: 'Pore/fracture pressure profile points (PPFG).',
    temperature: 'UDT profile points.'
  } as const;

  public readonly alwaysImportedItems = [
    'Fluids (created/reused from DrillPlan data)',
    'Datum and Water Depth',
    'Cementing (lead/tail densities, when available)'
  ] as const;

  public readonly importElapsedSeconds = signal(0);
  public readonly importStatusMessage = signal('Preparing import request...');
  public readonly importElapsedDisplay = computed(() => {
    const totalSeconds = this.importElapsedSeconds();
    const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  });

  public readonly canImport = computed(() =>
    this.isConnected() && !!this.selectedPlanId() &&
    (this.importTrajectory() || this.importWellboreGeometry() || this.importFormationMarkers() ||
     this.importPressureWindow() || this.importTemperature())
  );

  private readonly _destroy$ = new Subject<void>();
  private readonly _importStatusMessages = [
    'Contacting DrillPlan and gathering artifacts... ',
    'Parsing and mapping import data... ',
    'Applying updates to your design... ',
    'Finalizing import and preparing summary... '
  ] as const;
  private _importProgressTimer: ReturnType<typeof setInterval> | null = null;
  private _signalRSubscribed = false;
  private _importCompletionHandled = false;

  constructor(
    private readonly _drillPlanService: DrillPlanService,
    private readonly _notifications: AppNotificationService,
    private readonly _signalRService: SignalRService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.ensureSignalRSubscription();
      this.checkConnection();
    }
  }

  private checkConnection(): void {
    this.isCheckingConnection.set(true);
    this._drillPlanService.checkAuthStatus().pipe(takeUntil(this._destroy$)).subscribe({
      next: isConnected => {
        this._drillPlanService.isConnected.set(isConnected);
        this.isConnected.set(isConnected);
        this.isCheckingConnection.set(false);
        if (isConnected) {
          this.loadProjects();
        }
      },
      error: () => {
        this.isConnected.set(false);
        this.isCheckingConnection.set(false);
      }
    });
  }

  private loadProjects(): void {
    this.isLoadingProjects.set(true);
    this._drillPlanService.getProjects().pipe(takeUntil(this._destroy$)).subscribe({
      next: projects => {
        this.projects.set(projects);
        this.isLoadingProjects.set(false);
      },
      error: () => {
        this._notifications.showError('Failed to load DrillPlan projects.');
        this.isLoadingProjects.set(false);
      }
    });
  }

  connect(): void {
    this.isConnecting.set(true);
    this._drillPlanService.openAuthPopup().pipe(
      switchMap(code => this._drillPlanService.exchangeCode(code)),
      switchMap(() => this._drillPlanService.getContext()),
      takeUntil(this._destroy$)
    ).subscribe({
      next: () => {
        this._drillPlanService.isConnected.set(true);
        this.isConnected.set(true);
        this.isConnecting.set(false);
        this.loadProjects();
        this._notifications.showSuccess('Connected to DrillPlan.');
      },
      error: () => {
        this.isConnecting.set(false);
        this._notifications.showError('Failed to connect to DrillPlan.');
      }
    });
  }

  onProjectChange(projectId: string): void {
    this.selectedProjectId.set(projectId);
    this.selectedPlanId.set(null);
    this.plans.set([]);

    if (!projectId) return;

    this.isLoadingPlans.set(true);
    this._drillPlanService.getPlans(projectId).pipe(takeUntil(this._destroy$)).subscribe({
      next: plans => {
        this.plans.set(plans);
        this.isLoadingPlans.set(false);
      },
      error: () => {
        this._notifications.showError('Failed to load DrillPlan plans.');
        this.isLoadingPlans.set(false);
      }
    });
  }

  import(): void {
    const planId = this.selectedPlanId();
    if (!planId) return;

    this._importCompletionHandled = false;
    this.isImporting.set(true);
    this.startImportProgressUpdates();

    this._drillPlanService.importFromPlan(planId, {
      importTrajectory: this.importTrajectory(),
      importWellboreGeometry: this.importWellboreGeometry(),
      importFormationMarkers: this.importFormationMarkers(),
      importPressureWindow: this.importPressureWindow(),
      importTemperature: this.importTemperature()
    }).pipe(takeUntil(this._destroy$)).subscribe({
      next: (result: DrillPlanImportResult) => {
        if (result?.report) {
          this.completeImport(result);
          return;
        }

        this.importStatusMessage.set('Import job started. Waiting for completion signal...');
        this._notifications.showInfo(result.summary || 'DrillPlan import started. You will be notified when it finishes.');
      },
      error: () => {
        this.stopImportProgressUpdates();
        this.isImporting.set(false);
        this._notifications.showError('Import failed to start. Please try again.');
      }
    });
  }

  private completeImport(result: DrillPlanImportResult): void {
    if (this._importCompletionHandled) {
      return;
    }

    this._importCompletionHandled = true;
    this.stopImportProgressUpdates();
    this.isImporting.set(false);
    this.importResult.set(result);
    this.importComplete.emit(result);
    this._notifications.showSuccess(result.summary || 'DrillPlan import completed.');
  }

  private ensureSignalRSubscription(): void {
    if (this._signalRSubscribed) {
      return;
    }

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(
      hub,
      SignalRService.ON_PFB_CHANGE,
      data => this.handleSignalRNotice(data));
    this._signalRSubscribed = true;
  }

  private handleSignalRNotice(data: any): void {
    if (data?.action !== PeriforOnChangeMessages.DRILLPLAN_IMPORT_COMPLETED) {
      return;
    }

    if (!this.isImporting()) {
      return;
    }

    this.completeImport({
      summary: 'DrillPlan import completed.',
      report: null
    });
  }

  private startImportProgressUpdates(): void {
    this.stopImportProgressUpdates();

    this.importElapsedSeconds.set(0);
    this.importStatusMessage.set(this._importStatusMessages[0]);

    this._importProgressTimer = setInterval(() => {
      const elapsed = this.importElapsedSeconds() + 1;
      this.importElapsedSeconds.set(elapsed);

      const messageIndex = Math.floor(elapsed / 8) % this._importStatusMessages.length;
      this.importStatusMessage.set(this._importStatusMessages[messageIndex]);
    }, 1000);
  }

  private stopImportProgressUpdates(): void {
    if (this._importProgressTimer) {
      clearInterval(this._importProgressTimer);
      this._importProgressTimer = null;
    }
  }

  downloadArtifacts(): void {
    const planId = this.selectedPlanId();
    if (!planId) return;

    this.isDownloadingArtifacts.set(true);
    this._drillPlanService.downloadArtifacts(planId).pipe(takeUntil(this._destroy$)).subscribe({
      next: result => {
        this.isDownloadingArtifacts.set(false);
        this._notifications.showSuccess(`Downloaded ${result.downloaded} artifact(s) to ${result.outputFolder}`);
        if (result.failed > 0) {
          this._notifications.showWarning(`${result.failed} artifact(s) failed to download.`);
        }
      },
      error: () => {
        this.isDownloadingArtifacts.set(false);
        this._notifications.showError('Artifact download failed.');
      }
    });
  }

  copyReport(): void {
    const result = this.importResult();
    if (!result?.report) return;

    const lines: string[] = ['DrillPlan Import Report', ''];
    const report = result.report;

    if (report.applied.length > 0) {
      lines.push('✅ Applied');
      report.applied.forEach(i => lines.push(`  [${i.category}] ${i.message}`));
      lines.push('');
    }
    if (report.reviewRequired.length > 0) {
      lines.push('⚠️ Review recommended');
      report.reviewRequired.forEach(i => lines.push(`  [${i.category}] ${i.message}`));
      lines.push('');
    }
    if (report.actionRequired.length > 0) {
      lines.push('✏️ Action required');
      report.actionRequired.forEach(i => lines.push(`  [${i.category}] ${i.message}`));
      lines.push('');
    }
    if (report.notImported.length > 0) {
      lines.push('ℹ️ Not imported');
      report.notImported.forEach(i => lines.push(`  [${i.category}] ${i.message}`));
    }

    navigator.clipboard.writeText(lines.join('\n')).then(() =>
      this._notifications.showSuccess('Import report copied to clipboard.'));
  }

  close(): void {
    this.importResult.set(null);
    this.visibleChange.emit(false);
  }

  onVisibleChange(visible: boolean): void {
    this.visibleChange.emit(visible);
  }

  ngOnDestroy(): void {
    this.stopImportProgressUpdates();
    this._destroy$.next();
    this._destroy$.complete();
  }
}
